import { zxcvbn, zxcvbnOptions } from "@zxcvbn-ts/core";
import zxcvbnCommonPackage from "@zxcvbn-ts/language-common";
import zxcvbnJaPackage from "@zxcvbn-ts/language-ja";
import { css } from "@emotion/react";

const options = {
  translations: zxcvbnJaPackage.translations,
  graphs: zxcvbnCommonPackage.adjacencyGraphs,
  dictionary: {
    ...zxcvbnCommonPackage.dictionary,
    ...zxcvbnJaPackage.dictionary,
  },
};

type Props = {
  password: string;
  labelText?: string;
};

export const PasswordMeter = ({ password, labelText }: Props) => {
  zxcvbnOptions.setOptions(options);
  const re = zxcvbn(password);

  const widthOfPasswordMeter = () => {
    if (!isPasswordOf8OrMoreCharactoers()) return 0;
    switch (re.score) {
      case 0:
        // パスワードが8文字以上、かつscoreが0の場合はメーターの幅を20%とする
        return isPasswordOf8OrMoreCharactoers() ? 20 : 0;
      case 1:
        return 40;
      case 2:
        return 60;
      case 3:
        return 80;
      case 4:
        return 100;
      default:
        return 0;
    }
  };

  const createPassLabel = () => {
    switch (re.score) {
      case 0:
        return "弱い";
      case 1:
        return "弱い";
      case 2:
        return "普通";
      case 3:
        return "強い";
      case 4:
        return "非常に強い";
      default:
        return "";
    }
  };

  const isPasswordOf8OrMoreCharactoers = () => {
    if (!password) return false;
    return password.length >= 8;
  };

  const feedbackExists = () => {
    if (!re.feedback) return false;

    const warningExists = re.feedback.warning !== "";
    const suggestionsExists = re.feedback.suggestions.length !== 0;
    return warningExists || suggestionsExists;
  };

  const createWarningText = () => {
    if (!re.feedback) return "";
    return re.feedback.warning;
  };

  const createSuggestionsTextList = () => {
    if (!re.feedback) return [];
    return re.feedback.suggestions;
  };

  const changeColor = () => {
    switch (re.score) {
      case 0:
        return "#D32F2F";
      case 1:
        return "#DF732C";
      case 2:
        return "#EDC04B";
      case 3:
        return "#C2C951";
      case 4:
        return "#397D49";
      default:
        return "none";
    }
  };

  const changePasswordColor = () => ({
    width: `${widthOfPasswordMeter()}%`,
    background: changeColor(),
    height: "5px",
    borderRadius: "10px",
  });

  return (
    <>
      <div className="progress" css={progress}>
        <div
          className="progress-bar"
          style={changePasswordColor()}
          css={progressBar}
        ></div>
      </div>
      <div css={passwordStrengthField}>
        {isPasswordOf8OrMoreCharactoers() ? (
          <>
            <div css={{ display: "flex" }}>
              <p>パスワードの強度：</p>
              <p css={label}>{createPassLabel()}</p>
            </div>
            {feedbackExists() && (
              <div css={alertTextField}>
                <p css={warningText}>{createWarningText()}</p>
                {createSuggestionsTextList().map((suggestText, i) => {
                  return (
                    <p css={{ lineHeight: "1" }} key={i}>
                      ・{suggestText}
                    </p>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <>
            <div css={{ display: "flex" }}>
              <p css={defaultLabel}>
                ８文字以上の半角英数字または半角記号で入力してください
                {labelText && (
                  <>
                    <br />
                    {labelText}
                  </>
                )}
              </p>
            </div>
          </>
        )}
      </div>
    </>
  );
};

const progress = css({
  height: "5px",
  width: "100%",
  margin: "8px 0 0 0",
  borderRadius: "10px",
  backgroundColor: "#F1F3F4",
  position: "relative",
});

const progressBar = css({
  position: "absolute",
});

const passwordStrengthField = css({
  fontSize: "12px",
  lineHeight: "8px",
});

const label = css({
  color: "#030303",
});

const alertTextField = css({
  backgroundColor: "#F9F9F9",
  borderRadius: "4px",
  padding: "1px 16px",
  width: "100%",
});

const warningText = css({
  fontWeight: "600",
});

const defaultLabel = css({
  color: "#7E848E",
  lineHeight: "1.5",
});
